import { useState, useContext } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import { Loader2 } from "lucide-react";
import PropTypes from "prop-types";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AuthContext from "../context/AuthContext";
import { authApi } from "../services/api";
import { GOOGLE_CLIENT_ID } from "../constants";

// Isolated so `useGoogleLogin` is only invoked when GoogleOAuthProvider is
// mounted (App.jsx only mounts it when GOOGLE_CLIENT_ID is set).
const GoogleSignInButton = ({ loading, onTokenResponse, onError }) => {
  const googleLogin = useGoogleLogin({
    onSuccess: onTokenResponse,
    onError,
  });

  return (
    <>
      <div className="my-6 flex items-center">
        <hr className="flex-1 border-border" />
        <span className="px-3 text-sm text-muted-foreground">or</span>
        <hr className="flex-1 border-border" />
      </div>

      <Button
        type="button"
        variant="outline"
        size="lg"
        onClick={() => googleLogin()}
        disabled={loading}
        className="w-full"
      >
        {loading ? (
          <>
            <Loader2 className="animate-spin" />
            Signing In...
          </>
        ) : (
          "Sign in with Google"
        )}
      </Button>
    </>
  );
};

GoogleSignInButton.propTypes = {
  loading: PropTypes.bool.isRequired,
  onTokenResponse: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

const LoginPage = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { login } = useContext(AuthContext);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await authApi.login(username, password);

      login(response.data.access);
      setUsername("");
      setPassword("");
    } catch (err) {
      console.error("Login error:", err);

      let errorMessage = "Login failed. Please try again.";
      if (err.response) {
        if (err.response.status === 401) {
          errorMessage = "Invalid username or password. Please try again.";
        } else if (err.response.status >= 500) {
          errorMessage = "Server error. Please try again later.";
        } else if (err.response.data?.detail) {
          errorMessage = err.response.data.detail;
        }
      } else if (err.request) {
        errorMessage =
          "Unable to connect to server. Please check your internet connection.";
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleTokenResponse = async (tokenResponse) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authApi.googleLogin(tokenResponse.access_token);
      login(response.data.access, response.data.user);
    } catch (err) {
      console.error("Google login error:", err);
      setError("Google sign-in failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError("Google sign-in was cancelled or failed.");
  };

  const hasCredentials = Boolean(username.trim() && password.trim());

  return (
    <div className="page-container flex min-h-screen items-center bg-background py-12">
      <div className="mx-auto w-full max-w-md">
        <Card className="shadow-lg">
          <CardContent className="p-4 sm:p-8">
            <div className="mb-6 text-center">
              <h1 className="mb-2 font-display text-2xl font-bold text-primary">
                Welcome to Mono
              </h1>
              <p className="text-muted-foreground">
                Your personal knowledge management system
              </p>
            </div>

            {error && (
              <Alert variant="destructive" id="login-error" className="mb-4">
                <AlertDescription className="flex items-center gap-2">
                  <span aria-hidden="true">⚠️</span>
                  {error}
                </AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <div className="mb-4 grid gap-1.5">
                <Label htmlFor="username" className="font-semibold">
                  Username <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoFocus
                  disabled={loading}
                  aria-describedby={error ? "login-error" : undefined}
                  className="h-11"
                />
              </div>

              <div className="mb-6 grid gap-1.5">
                <Label htmlFor="password" className="font-semibold">
                  Password <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  aria-describedby={error ? "login-error" : undefined}
                  className="h-11"
                />
              </div>

              <Button
                type="submit"
                size="lg"
                disabled={loading || !hasCredentials}
                className="h-11 w-full md:h-10"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" />
                    Signing In...
                  </>
                ) : (
                  "Sign In"
                )}
              </Button>

              <p className="mt-3 text-center text-sm text-muted-foreground">
                {hasCredentials
                  ? "✅ Ready to sign in"
                  : "Please enter your credentials to continue"}
              </p>
            </form>

            {GOOGLE_CLIENT_ID && (
              <GoogleSignInButton
                loading={loading}
                onTokenResponse={handleGoogleTokenResponse}
                onError={handleGoogleError}
              />
            )}
          </CardContent>
        </Card>

        {/* Footer info */}
        <p className="mt-4 text-center text-sm text-muted-foreground">
          Build your knowledge base with spaced repetition learning
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
